import type { ReactNode } from "react";

export type BadgeTone = "neutral" | "good" | "warning" | "danger" | "info";

export function StatusBadge({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: BadgeTone;
}): JSX.Element {
  const styles = {
    neutral: "border-slate-200 bg-slate-50 text-slate-600",
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    danger: "border-rose-200 bg-rose-50 text-rose-700",
    info: "border-sky-200 bg-sky-50 text-sky-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${styles[tone]}`}
    >
      {label}
    </span>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  tone = "default",
}: {
  label: string;
  value: string | number;
  detail: string;
  tone?: "default" | "warning";
}): JSX.Element {
  return (
    <div
      className={`surface-card border-l-4 p-4 ${tone === "warning" ? "border-l-amber-500" : "border-l-slate-300"}`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
        {value}
      </div>
      <div className="mt-1 text-xs text-slate-500">{detail}</div>
    </div>
  );
}

export function EmptyState({
  title,
  detail,
  action,
  compact = false,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
  compact?: boolean;
}): JSX.Element {
  return (
    <div className={compact ? 'py-1' : 'rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center'}>
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      <div className={`${compact ? 'mt-1' : 'mx-auto mt-2 max-w-xl'} text-sm leading-6 text-slate-500`}>
        {detail}
      </div>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function PlaceholderPanel({
  title,
  detail,
  label = "Not connected",
}: {
  title: string;
  detail: string;
  label?: string;
}): JSX.Element {
  return (
    <section className="surface-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
            {detail}
          </p>
        </div>
        <StatusBadge label={label} tone="neutral" />
      </div>
      <div className="mt-6">
        <EmptyState
          title="Data connection is not active"
          detail="This workspace is intentionally read-only until its approved data connection is available. No placeholder records are shown."
        />
      </div>
    </section>
  );
}

export function SubTabs({
  items,
  active,
  onChange,
}: {
  items: string[];
  active: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div
      className="flex gap-1 overflow-x-auto border-b border-slate-200"
      role="tablist"
      aria-label="Section navigation"
    >
      {items.map((item) => (
        <button
          key={item}
          type="button"
          role="tab"
          aria-selected={active === item}
          onClick={() => onChange(item)}
          className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition ${active === item ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

export function DetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
        {title}
      </h3>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        {children}
      </div>
    </section>
  );
}

export function DrawerSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <h3 className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-600">{title}</h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function DetailGrid({
  items,
}: {
  items: Array<[string, string]>;
}): JSX.Element {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </dt>
          <dd className="mt-1 break-words text-sm text-slate-800">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
